import { useState } from "react";
import {
  Popover, ActionIcon, Stack, Group, Text, Button, Loader, Tooltip, Divider,
} from "@mantine/core";
import { IconHistory, IconRestore } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { getFieldHistory, restoreEntry } from "../api";
import ProvenanceBadge from "./ProvenanceBadge";
import { actorLabel } from "../_Shared/activity";

// Entry fields to diff, in display order, paired with their existing i18n label key.
const FIELD_DEFS = {
  career: [["role", "role"], ["companyName", "company"], ["from", "from"], ["to", "to"],
    ["location", "location"], ["description", "description"]],
  education: [["description", "description"], ["year", "year"]],
};

// Snapshots are camelCase, but tolerate legacy PascalCase ones written before the casing was settled.
function camelKeys(o) {
  if (!o || typeof o !== "object") return o;
  const r = {};
  for (const k of Object.keys(o)) r[k.charAt(0).toLowerCase() + k.slice(1)] = o[k];
  return r;
}
function parseSnap(e) {
  try { return e.entrySnapshot ? camelKeys(JSON.parse(e.entrySnapshot)) : null; } catch { return null; }
}
const clip = (s) => (s.length > 140 ? s.slice(0, 140) + "…" : s);

// Fields whose value differs between this version (curr) and the older one (prev). prev=null → the
// earliest version, so every set field reads as newly established.
function changedFields(curr, prev, defs) {
  const norm = (v) => (v ?? "").toString().trim();
  return defs
    .filter(([k]) => norm(curr?.[k]) !== norm(prev?.[k]))
    .map(([k, labelKey]) => ({ key: k, labelKey, before: norm(prev?.[k]), after: norm(curr?.[k]) }));
}

/**
 * Per-entry provenance badge + a history popover for one career/education entry. The popover shows a
 * field-level diff per version (what changed, old → new), who/what changed it, and when — each prior
 * version restorable. Restore POSTs the stored snapshot back and is itself change-tracked.
 * `onRestored` receives the updated candidate.
 */
export default function EntryHistory({ candidateId, field, entryId, sourceKind, onRestored }) {
  const { t, i18n } = useTranslation("Veft");
  const [opened, setOpened] = useState(false);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const defs = FIELD_DEFS[field] ?? [];

  async function load() {
    setLoading(true);
    try {
      const evs = await getFieldHistory(candidateId, field, entryId);
      setEvents(Array.isArray(evs) ? evs : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    const next = !opened;
    setOpened(next);
    if (next && events === null) load();
  }

  async function doRestore(activityId) {
    setRestoringId(activityId);
    try {
      const updated = await restoreEntry(candidateId, activityId);
      onRestored?.(updated);
      notifications.show({ color: "green", message: t("history_restored") });
      setOpened(false);
      setEvents(null); // force a refetch next open
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: "" + (e?.message || e) });
    } finally {
      setRestoringId(null);
    }
  }

  const fmtDate = (at) => new Date(at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={360} withArrow shadow="md" trapFocus>
      <Popover.Target>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          <ProvenanceBadge sourceKind={sourceKind} />
          <Tooltip label={t("history_view")} withArrow>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={toggle}>
              <IconHistory size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm" fw={500} mb="xs">{t("history_title")}</Text>
        {loading ? (
          <Group gap="xs"><Loader size="xs" /><Text size="xs" c="dimmed">{t("history_loading")}</Text></Group>
        ) : !events || events.length === 0 ? (
          <Text size="xs" c="dimmed">{t("history_empty")}</Text>
        ) : (
          <Stack gap={6}>
            {events.map((e, i) => {
              const curr = parseSnap(e);
              const prev = i + 1 < events.length ? parseSnap(events[i + 1]) : null;
              const changes = curr ? changedFields(curr, prev, defs) : [];
              return (
                <div key={e.id}>
                  {i > 0 && <Divider mb={6} />}
                  <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
                    <Text size="xs" c="dimmed">{actorLabel(e, t)} · {fmtDate(e.at)}</Text>
                    {i > 0 && e.canRestore && (
                      <Button size="compact-xs" variant="subtle" leftSection={<IconRestore size={12} />}
                        loading={restoringId === e.id} onClick={() => doRestore(e.id)} style={{ flexShrink: 0 }}>
                        {t("history_restore")}
                      </Button>
                    )}
                  </Group>
                  {e.currentValue == null ? (
                    <Text size="xs" c="red" mt={2}>{t("history_removed")}</Text>
                  ) : changes.length > 0 ? (
                    <Stack gap={1} mt={2}>
                      {changes.map((c) => (
                        <Text size="xs" key={c.key} style={{ whiteSpace: "pre-wrap" }}>
                          <Text span fw={500}>{t(c.labelKey)}:</Text>{" "}
                          {c.before && <><Text span c="dimmed" td="line-through">{clip(c.before)}</Text>{" → "}</>}
                          {c.after ? clip(c.after) : <Text span c="dimmed">{t("history_blank")}</Text>}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    // No snapshot (legacy event) or no detectable field change → fall back to the label.
                    <Text size="xs" c="dimmed" mt={2}>{e.currentValue}</Text>
                  )}
                </div>
              );
            })}
          </Stack>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
